/* eslint-disable @typescript-eslint/ban-ts-comment */
import * as React from 'react'
import { useHistory } from 'react-router'
import { toast } from 'react-toastify'

import { useFilters } from '@/hooks/useQuery'
import FormsPageContent from '@/pages/FormsPage/FormsPageContent'
import { useLoadPackets } from '@/queries/packets/useLoadPackets'
import type { Packet, PacketQuery } from '@/types/packet'
import { ContextTabletMode } from '@/utils/ContextTabletMode'
import { ZentakeContext } from '@/utils/ZentakeContext'
type Props = {
  tabletMode?: boolean
}

export function PacketsPage({ tabletMode = false }: Props): React.ReactNode {
  const history = useHistory()
  const [loading, setLoading] = React.useState(false)
  const [search, setSearch] = React.useState('')
  const { filters } = useFilters<PacketQuery>({
    defaultValues: { page: 1, pageSize: 25 },
  })
  const {
    data: packets,
    isLoading,
    isPreviousData,
    isFetching,
  } = useLoadPackets(
    {
      ...filters,
      search,
      simplified: 'true',
      without_disabled: tabletMode ? 'true' : 'false',
    },
    {
      staleTime: tabletMode ? 0 : Infinity,
      keepPreviousData: true,
    }
  )
  const packetsLoading = isLoading || isFetching || isPreviousData

  const { isFetchingForms, userDetails } = React.useContext(ZentakeContext)

  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
  const tabletModeContext = React.useContext(ContextTabletMode)

  const [packetName, setPacketName] = React.useState<string | null>(null)
  const [hasUrlModal, setHasUrlModal] = React.useState(false)
  const [publicUrl, setPublicUrl] = React.useState<string | null>('')
  const [selectedResources, setSelectedResources] = React.useState<
    { id: string }[]
  >([])

  const sortPackets = (packetsToSort: Packet[]) =>
    packetsToSort?.sort((a, b) => a.name?.localeCompare(b.name))
  const listToShow = sortPackets(
    packets?.results?.filter((packet) => packet.enabled) || []
  )
  const inactivePackets = sortPackets(
    packets?.results?.filter((packet) => !packet.enabled) || []
  )

  const primaryAction = React.useCallback(() => {
    history.push('/packets/new')
  }, [history])

  const handleItemClick = React.useCallback(
    (item: Packet) => {
      history.push(`/packets/${item.id}`)
    },
    [history]
  )

  const onPublicFormClick = React.useCallback(
    (id: string, name: string, type: string) => {
      if (!userDetails?.is_verified) {
        toast.warning('Verify Email')
        return
      }

      setHasUrlModal(true)
      setPublicUrl(`${window.location.origin}/f/${id}`)
      setPacketName(name)
    },
    [userDetails?.is_verified]
  )

  const onPublicFormDismiss = React.useCallback(() => {
    setHasUrlModal(false)
    setPublicUrl(null)
    setPacketName(null)
  }, [])

  const onSelectResource = React.useCallback(
    (resourceId: string) => {
      if (selectedResources?.find((resource) => resource.id === resourceId)) {
        setSelectedResources([])

        return
      }

      setSelectedResources([{ id: resourceId }])
    },
    [selectedResources]
  )

  const navigateToStartPacket = React.useCallback(() => {
    if (!tabletModeContext) return

    // @ts-ignore
    const { createFillSession } = tabletModeContext

    const selectedPacketId = selectedResources?.[0]?.id
    if (!selectedPacketId) return

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    const newSessionId = createFillSession({
      queue: [{ id: selectedPacketId }],
      type: 'packet',
    })

    history.push(`/t/p/${newSessionId}`)
  }, [history, selectedResources, tabletModeContext])

  const items = React.useMemo(
    () => [...listToShow, ...(tabletMode ? [] : inactivePackets)],
    [inactivePackets, listToShow, tabletMode]
  )

  return (
    <>
      <FormsPageContent
        isPacket
        formSearch={search}
        handleItemClick={handleItemClick}
        hasUrlModal={hasUrlModal}
        isEmpty={!items.length}
        isFetchingForms={isFetchingForms}
        items={items}
        itemsCount={packets?.count || 0}
        loading={packetsLoading || isFetchingForms || loading}
        navigateToStartForms={navigateToStartPacket}
        onPublicFormClick={onPublicFormClick}
        onPublicFormDismiss={onPublicFormDismiss}
        onSelectResource={onSelectResource}
        primaryAction={primaryAction}
        primaryActionDataCy="create-packet"
        primaryActionText="Create Packet"
        publicFormName={packetName}
        publicUrl={publicUrl}
        selectedResources={selectedResources}
        setFormSearch={setSearch}
        setLoading={setLoading}
        tabletMode={tabletMode}
        title="Packets"
      />
    </>
  )
}

export default PacketsPage
